/**
 * ScenarioMotionRecorder - PNG 프레임 시퀀스 캡처 도구
 * ScenarioMotion 애니메이션을 프레임별로 PNG로 저장
 */

import React, { useState, useEffect, useRef } from "react";
import ScenarioMotion from "./ScenarioMotion";
import type { MotionScenario } from "./types";

interface ScenarioMotionRecorderProps {
  scenario: MotionScenario;
  title: string;
  description: string;
  frameRate?: number; // 초당 프레임 수 (기본값: 30)
  width?: number; // 캡처 너비 (기본값: 800)
  height?: number; // 캡처 높이 (기본값: 600)
  autoStart?: boolean; // 자동 시작 여부 (기본값: false)
  onComplete?: () => void; // 캡처 완료 콜백
  filePrefix?: string; // 파일명 접두사 (기본값: "motion")
}

export const ScenarioMotionRecorder: React.FC<ScenarioMotionRecorderProps> = ({
  scenario,
  title,
  description,
  frameRate = 30,
  width = 800,
  height = 600,
  autoStart = false,
  onComplete,
  filePrefix = "motion",
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [capturedFrames, setCapturedFrames] = useState<string[]>([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // 애니메이션 총 시간 계산
  const transitionSpeed = scenario.transitionSpeed ?? 1500;
  const sceneCount = scenario.scenes.length;
  const totalDuration = transitionSpeed * sceneCount;
  const frameInterval = 1000 / frameRate; // ms per frame
  const totalFrames = Math.ceil(totalDuration / frameInterval);

  // 프레임 캡처 시작
  const startRecording = () => {
    setCapturedFrames([]);
    setCurrentFrame(0);
    setIsRecording(true);
    setIsHovered(true); // 애니메이션 시작
  };

  // 프레임 캡처 실행
  useEffect(() => {
    if (!isRecording || !containerRef.current || !canvasRef.current) return;

    const captureFrame = async () => {
      if (currentFrame >= totalFrames) {
        // 캡처 완료
        setIsRecording(false);
        setIsHovered(false);

        // 완료 콜백 호출
        if (onComplete) {
          onComplete();
        }
        return;
      }

      const container = containerRef.current!;
      const canvas = canvasRef.current!;
      const ctx = canvas.getContext("2d");

      if (!ctx) return;

      // SVG를 이미지로 변환
      const svgElement = container.querySelector("svg");
      if (!svgElement) return;

      // SVG의 실제 크기 가져오기
      const svgRect = svgElement.getBoundingClientRect();
      const svgWidth = svgRect.width;
      const svgHeight = svgRect.height;

      // 캔버스 크기를 SVG 비율로 조정
      canvas.width = svgWidth;
      canvas.height = svgHeight;

      // SVG를 직렬화 (중요 스타일만 인라인화)
      const serializer = new XMLSerializer();
      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;

      // 중요한 스타일 속성 확장 (시각 효과 포함)
      const importantProps = [
        "fill",
        "fill-opacity",
        "stroke",
        "stroke-width",
        "stroke-opacity",
        "stroke-dasharray",
        "stroke-dashoffset",
        "stroke-linecap",
        "stroke-linejoin",
        "opacity",
        "transform",
        "transform-origin",
        "filter",
        "clip-path",
        "mask",
        "visibility",
        "display",
        "color",
        "font-size",
        "font-family",
        "font-weight",
        "text-anchor",
        "dominant-baseline",
        "stop-color",
        "stop-opacity",
        "offset",
      ];

      const inlineStyles = (element: Element, sourceElement: Element) => {
        const computedStyle = window.getComputedStyle(sourceElement);
        let styleString = "";

        importantProps.forEach((prop) => {
          const value = computedStyle.getPropertyValue(prop);
          if (value && value !== "none" && value !== "normal") {
            styleString += `${prop}:${value};`;
          }
        });

        if (styleString) {
          element.setAttribute("style", styleString);
        }

        // 자식 요소들도 재귀적으로 처리
        const children = element.children;
        const sourceChildren = sourceElement.children;
        for (let i = 0; i < children.length && i < sourceChildren.length; i++) {
          inlineStyles(children[i], sourceChildren[i]);
        }
      };

      inlineStyles(clonedSvg, svgElement);

      const svgString = serializer.serializeToString(clonedSvg);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      // 이미지로 로드
      const img = new Image();
      img.onload = () => {
        // 캔버스에 그리기 (원본 비율 유지)
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#fcf7faff"; // 배경색
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // PNG 데이터 URL로 변환
        const dataUrl = canvas.toDataURL("image/png");

        // 서버에 저장
        const filename = `${filePrefix}_frame_${String(currentFrame).padStart(
          4,
          "0"
        )}.png`;
        saveFrameToServer(dataUrl, filename);

        setCapturedFrames((prev) => [...prev, dataUrl]);
        setCurrentFrame((prev) => prev + 1);

        URL.revokeObjectURL(url);
      };

      img.src = url;
    };

    const timer = setTimeout(captureFrame, frameInterval);

    return () => clearTimeout(timer);
  }, [
    isRecording,
    currentFrame,
    totalFrames,
    frameInterval,
    onComplete,
    filePrefix,
  ]);

  // 개별 프레임 저장 (서버에 저장)
  const saveFrameToServer = async (dataUrl: string, filename: string) => {
    try {
      const response = await fetch("http://localhost:5000/api/save-frame", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dataUrl,
          filename,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Failed to save frame:", error);
      return null;
    }
  };

  // 개별 프레임 다운로드 (브라우저)
  const downloadFrame = (dataUrl: string, index: number) => {
    const link = document.createElement("a");
    link.download = `${filePrefix}_frame_${String(index).padStart(4, "0")}.png`;
    link.href = dataUrl;
    link.click();
  };

  // 모든 프레임 다운로드
  const downloadAllFrames = () => {
    capturedFrames.forEach((dataUrl, index) => {
      setTimeout(() => {
        downloadFrame(dataUrl, index);
      }, index * 100); // 각 다운로드 사이에 100ms 간격
    });
  };

  // 자동 시작
  useEffect(() => {
    if (autoStart) {
      startRecording();
    }
  }, [autoStart]);

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ marginBottom: "10px" }}>ScenarioMotion Recorder</h2>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          프레임레이트: {frameRate}fps | 총 프레임: {totalFrames}
        </p>

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <button
            onClick={startRecording}
            disabled={isRecording}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              backgroundColor: isRecording ? "#ccc" : "#667eea",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: isRecording ? "not-allowed" : "pointer",
            }}
          >
            {isRecording
              ? `캡처 중... (${currentFrame}/${totalFrames})`
              : "캡처 시작"}
          </button>

          {capturedFrames.length > 0 && (
            <button
              onClick={downloadAllFrames}
              style={{
                padding: "10px 20px",
                fontSize: "16px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              모든 프레임 다운로드 ({capturedFrames.length}개)
            </button>
          )}
        </div>
      </div>

      {/* 캡처용 캔버스 (숨김) - 크기는 런타임에 SVG 비율에 맞춰 동적 조정됨 */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* 미리보기 */}
      <div
        ref={containerRef}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          border: "2px solid #ddd",
          borderRadius: "8px",
          overflow: "hidden",
          marginBottom: "20px",
          backgroundColor: "#fcf7faff",
        }}
      >
        <ScenarioMotion
          scenario={scenario}
          title={title}
          description={description}
          isHovered={isHovered}
        />
      </div>

      {/* 캡처된 프레임 갤러리 */}
      {capturedFrames.length > 0 && (
        <div>
          <h3 style={{ marginBottom: "10px" }}>
            캡처된 프레임 ({capturedFrames.length})
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "10px",
            }}
          >
            {capturedFrames.map((dataUrl, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={() => downloadFrame(dataUrl, index)}
                title={`프레임 ${index} 클릭하여 다운로드`}
              >
                <img
                  src={dataUrl}
                  alt={`Frame ${index}`}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
                <div
                  style={{
                    padding: "5px",
                    fontSize: "12px",
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  #{index}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioMotionRecorder;
